import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Chip,
    Stack
} from '@mui/material';
import { Folder as FolderIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { hasCapability, CAP } from '../../../utils/permissions';
import { projectsService } from '../../../services/projectsService';
import { Project } from '../../../types';
import { isUserOnProjectTeam } from '../../../utils/projectTeam';

const RecentProjectsWidget: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProjects = async () => {
            if (!user) return;

            try {
                setLoading(true);
                const allProjects = await projectsService.getProjects();

                // PM/Admin/Director users see all projects, others only see projects they're PM or engineer of
                const canAccessAllProjects = hasCapability(user, CAP.VIEW_ALL_PROJECTS);
                let projectsToShow: Project[];

                if (canAccessAllProjects) {
                    projectsToShow = allProjects;
                } else {
                    projectsToShow = allProjects.filter(project => isUserOnProjectTeam(project, user));
                }

                // Sort by ID descending (most recent first) and take top 5
                const recentProjects = projectsToShow
                    .sort((a, b) => b.id - a.id)
                    .slice(0, 5);

                setProjects(recentProjects);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch projects', err);
                setError('Failed to load projects');
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [user]);

    const handleProjectClick = (projectId: number) => {
        navigate(`/projects/${projectId}/documents`);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (projects.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No projects assigned to you yet
                </Typography>
            </Box>
        );
    }

    return (
        <List disablePadding>
            {projects.map((project) => (
                <ListItem key={project.id} disablePadding divider>
                    <ListItemButton onClick={() => handleProjectClick(project.id)}>
                        <FolderIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <ListItemText
                            primary={
                                <Typography variant="body1" fontWeight={500}>
                                    {project.name}
                                </Typography>
                            }
                            secondary={
                                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                    {project.client_name && (
                                        <Typography variant="caption" color="text.secondary">
                                            {project.client_name}
                                        </Typography>
                                    )}
                                    {project.status && (
                                        <Chip
                                            label={project.status}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Stack>
                            }
                        />
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
};

export default RecentProjectsWidget;
